import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input, model, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { BaasCoreStateService, BarCoreService, BarMeldingType } from '@lvflanders/bar-ng';

import { BaasActionButtonOptions } from '../../baas-action-button/baas-action-button.types';
import { BaasHero } from '../../baas-hero/baas-hero';
import { BaasHeroOptions } from '../../baas-hero/baas-hero-options';
import { BaasLink } from '../../baas-link/baas-link';
import { BaasMenuButtonItemOptions } from '../../baas-menu-button/baas-menu-button-item.types';
import { BarI18nPipe } from '../../i18n/bar-i18n.pipe';
import { _BaasInternalLayoutService } from '../../layout/baas-internal-layout.service';
import { BaasLayoutService } from '../../layout/baas-layout/baas-layout.service';
import { BaasMeldingen } from '../../meldingen/baas-meldingen/baas-meldingen';
import { BaasExternalLinkGroupOptions } from '../baas-external-link-group-options.type';
import { BarAppMenuItem } from '../bar-app-menu-item.type';
import { BarLayoutState } from '../bar-layout-state.contract';
import { BarMenuState } from '../bar-menu-state.type';
import { BAAS_LAYOUT_STATE } from '../layout-tokens';
import { BaasOffcanvas } from '../offcanvas/baas-offcanvas';
import { BaasOffcanvasOptions } from '../offcanvas/baas-offcanvas-options';
import { BaasMenuItemOptions } from './baas-layout-menu/baas-menu-item-options.type';
import { BaasLayoutPageComponent } from './baas-layout-page/baas-layout-page.component';

@Component({
    selector: 'baas-layout',
    templateUrl: './baas-layout.html',
    host: { class: 'bar-layout-root' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, NgClass, BaasLayoutPageComponent, BaasLink, BarI18nPipe, BaasMeldingen, BaasHero, BaasOffcanvas]
})
export class BaasLayout implements OnInit {
    readonly #layoutState = inject<BarLayoutState>(BAAS_LAYOUT_STATE);
    readonly #layout = inject(BaasLayoutService);
    readonly #coreState = inject(BaasCoreStateService, { optional: true });
    readonly #internalLayout = inject(_BaasInternalLayoutService);
    readonly #core = inject(BarCoreService);
    readonly #router = inject(Router, { optional: true });
    readonly #cdr = inject(ChangeDetectorRef);
    readonly #destroyRef = inject(DestroyRef);

    readonly touch = this.#layout.touch;
    readonly compact = this.#layout.compact;
    readonly mobileFriendly = this.#layout.mobileFriendly;
    readonly appVersion = this.#layout.appVersion;

    /** Will sync the data with the BaasLayoutService if set to true. This means that if you set an input property it will automatically be synced towards the layout provider. */
    readonly autosync = input(false);
    readonly withRouting = input(false);

    readonly box = input<boolean>(this.#internalLayout.box);

    readonly _title = input<string>(undefined, { alias: 'title' });
    protected readonly title = computed(() => {
        if (this._title() !== undefined) return this._title();
        return this.#layout.title();
    });

    readonly _subTitle = input<string>(undefined, { alias: 'subTitle' });
    protected readonly subTitle = computed(() => {
        if (this._subTitle() !== undefined) return this._subTitle();
        return this.#layout.subTitle();
    });

    readonly _sidebar = input<boolean>(undefined, { alias: 'sidebar' });
    protected readonly sidebar = computed(() => {
        if (this._sidebar() !== undefined) return this._sidebar();
        if (this.#layout.sidebar() !== undefined) return this.#layout.sidebar();
        return true;
    });

    readonly _submitActions = input<BaasActionButtonOptions[]>(undefined, { alias: 'submitActions' });
    protected readonly submitActions = computed(() => {
        if (this._submitActions() !== undefined) return this._submitActions();
        if (this.#layout.submitActions() !== undefined) return this.#layout.submitActions();
        return [];
    });

    readonly _moreActions = input<BaasMenuButtonItemOptions[]>(undefined, { alias: 'moreActions' });
    protected readonly moreActions = computed(() => {
        if (this._moreActions() !== undefined) return this._moreActions();
        if (this.#layout.moreActions() !== undefined) return this.#layout.moreActions();
        return [];
    });

    readonly _contentActions = input<BaasActionButtonOptions[]>(undefined, { alias: 'contentActions' });
    protected readonly contentActions = computed(() => {
        if (this._contentActions() !== undefined) return this._contentActions();
        if (this.#layout.contentActions() !== undefined) return this.#layout.contentActions();
        return [];
    });

    readonly _mainHeader = input<string>(undefined, { alias: 'mainHeader' });
    protected readonly mainHeader = computed(() => {
        if (this._mainHeader()) return this._mainHeader();
        if (this.#layout.mainHeader() !== undefined) return this.#layout.mainHeader();
        return this._mainHeader();
    });

    readonly _operations = input<BaasActionButtonOptions[]>(undefined, { alias: 'operations' });
    protected readonly operations = computed(() => {
        if (this._operations() !== undefined) return this._operations();
        if (this.#layout.operations() !== undefined) return this.#layout.operations();
        return [];
    });

    readonly _strikingActions = input<BaasActionButtonOptions[]>(undefined, { alias: 'strikingActions' });
    protected readonly strikingActions = computed(() => {
        if (this._strikingActions() !== undefined) return this._strikingActions();
        if (this.#layout.strikingActions() !== undefined) return this.#layout.strikingActions();
        return [];
    });

    readonly _externalLinkGroups = input<BaasExternalLinkGroupOptions[]>(undefined, { alias: 'externalLinkGroups' });
    protected readonly externalLinkGroups = computed(() => {
        if (this._externalLinkGroups() !== undefined) return this._externalLinkGroups();
        if (this.#layout.externalLinkGroups() !== undefined) return this.#layout.externalLinkGroups();
        return [];
    });

    readonly menuItems = input<BaasMenuItemOptions[]>(undefined);

    readonly _menuItemsAutoCollapse = input<boolean>(undefined, { alias: 'menuItemsAutoCollapse' });
    protected readonly menuItemsAutoCollapse = computed(() => {
        if (this._menuItemsAutoCollapse() !== undefined) return this._menuItemsAutoCollapse();
        if (this.#layout.menuItemsAutoCollapse() !== undefined) return this.#layout.menuItemsAutoCollapse();
        return false;
    });

    readonly menuState = model<BarMenuState>('default');
    readonly hideActionBarContentLeft = input<boolean>(undefined);

    readonly offcanvasOpened = model<boolean>(false);
    readonly offcanvasPinned = model<boolean>(false);
    readonly offcanvasOptions = input<BaasOffcanvasOptions>(undefined);
    protected readonly offcanvasWidth = computed(() => this.offcanvasOptions()?.width ?? 'default');

    readonly _appMenuItems = input<BarAppMenuItem[]>(undefined, { alias: 'appMenuItems' });
    protected readonly appMenuItems = computed(() => {
        if (this._appMenuItems() !== undefined) return this._appMenuItems();
        if (this.#layout.appMenuItems() !== undefined) return this.#layout.appMenuItems();
        return [];
    });
    protected readonly desktopAppMenuItems = computed(() => {
        return this.appMenuItems() ? this.appMenuItems().filter((ami) => !ami.hidden && (ami.mobile == null || ami.mobile === false)) : undefined;
    });
    protected readonly mobileAppMenuItems = computed(() => {
        return this.#layout.appMenuItems()
            ? [...this.#layout.appMenuItems().filter((ami) => !ami.hidden && (ami.mobile == null || ami.mobile === true))]
            : undefined;
    });

    protected readonly skipLink = signal<{ main: string; menu: string }>({ main: null, menu: null });

    protected readonly meldingen = computed(() => this.#coreState?.meldingen()?.filter((m) => m.meldingType != BarMeldingType.Banner) || []);

    protected readonly mobileItems = computed(() => {
        if (!this.mobileAppMenuItems()) return [];
        if (this.mobileAppMenuItems().length < 2) {
            return [];
        } else if (this.mobileAppMenuItems().length < 6) {
            return this.mobileAppMenuItems();
        } else {
            return [
                ...this.mobileAppMenuItems().slice(0, 4),
                <BarAppMenuItem>{
                    content: 'Meer',
                    icon: { srText: 'Meer', position: 'top', class: 'fas fa-ellipsis-h' },
                    action: (e) => this.menuOverlayOpen.set(true)
                }
            ];
        }
    });

    protected readonly restOfMobileItems = computed(() => {
        return this.mobileAppMenuItems().slice(4);
    });

    protected readonly menuOverlayOpen = signal(false);

    readonly _hero = input<BaasHeroOptions>(undefined, { alias: 'hero' });

    readonly #hero = computed(() => this._hero() || this.#layout.hero());

    protected readonly hero = computed(() => ({
        item: this.#hero(),
        // NOTE avoid effect by declaring signal in a computed (pattern from NG architect once showed in a youtube video)
        /** If an instance of hero is given it is shown by default. Whenever the user closes the hero this value will be set. */
        hidden: signal(!this.#hero())
    }));

    env: string;
    rootUrl: string;

    constructor() {
        this.#defineLayoutEffects();

        effect(() => {
            // TODO apparently first time there is no rerender in case this signal changes, investigate further
            if (this.meldingen()) this.#cdr.detectChanges();
        });

        effect(() => {
            if (this.menuState()) this.#internalLayout.setMenuState(this.menuState());
        });
    }

    ngOnInit(): void {
        this.env = this.#layoutState.environment;
        this.rootUrl = this.#layoutState.rootUrl;

        this.skipLink.set({ main: `${location.pathname}#maincontent`, menu: `${location.pathname}#mainmenu` });

        if (this.#router) {
            this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    // initial only executed once
                    // set up baselink, pathName is the url without origin and without hash, query,...
                    this.skipLink.set({ main: `${location.pathname}#maincontent`, menu: `${location.pathname}#mainmenu` });

                    // if Fragment is present, focus it (will scroll into view automatically)
                    if (event.urlAfterRedirects.indexOf('#') > -1) {
                        const fragment = event.urlAfterRedirects.substring(event.urlAfterRedirects.indexOf('#') + 1);
                        const element = document.getElementById(fragment);
                        // make sure tabindex (-1, 0) is present on the to be focussed element (otherwise it will not work);
                        if (element) element.focus();
                    }
                }
            });
        }
    }

    protected navigateHome(): boolean {
        // if not home url override (disconnected modus) we use the core functionality to navigate
        if (this.#layoutState.homeUrl == null) {
            this.#core.navigateHome();
            return false;
        }
        return true;
    }

    protected navigateRoot(): boolean {
        this.#core.navigateRoot();
        return false;
    }

    protected defineAppMenuLinkClass(item: BarAppMenuItem): string {
        return item.selected ? 'menu-lv__item menu-lv__item active' : 'menu-lv__item';
    }

    protected exitCompact() {
        this.#layout.compact.set(false);
    }

    protected onCloseHero(): void {
        this.hero().hidden.set(true);
    }

    protected closeOffcanvas(): void {
        this.offcanvasOpened.set(false);
        this.offcanvasPinned.set(false);
    }

    // NOTE here we need to keep the layout service nsync if autosync is enabled
    #defineLayoutEffects(): void {
        effect(() => {
            if (this._title() !== undefined && this.autosync()) this.#layout.title.set(this._title());
        });
        effect(() => {
            if (this._subTitle() !== undefined && this.autosync()) this.#layout.subTitle.set(this._subTitle());
        });
        effect(() => {
            if (this.#layout.menuState() !== undefined) {
                this.menuState.set(this.#layout.menuState());
            }
        });
        effect(() => {
            // NOTE geen autosync nodig hier
            if (this.menuState() !== undefined) this.#layout.menuState.set(this.menuState());
        });
        effect(() => {
            if (this._sidebar() !== undefined && this.autosync()) this.#layout.sidebar.set(this._sidebar());
        });
        effect(() => {
            if (this._submitActions() !== undefined && this.autosync()) this.#layout.submitActions.set(this._submitActions());
        });
        effect(() => {
            if (this._moreActions() !== undefined && this.autosync()) this.#layout.moreActions.set(this._moreActions());
        });
        effect(() => {
            if (this._contentActions() !== undefined && this.autosync()) this.#layout.contentActions.set(this._contentActions());
        });
        effect(() => {
            if (this._mainHeader() !== undefined && this.autosync()) this.#layout.mainHeader.set(this._mainHeader());
        });
        effect(() => {
            if (this._operations() !== undefined && this.autosync()) this.#layout.operations.set(this._operations());
        });
        effect(() => {
            if (this._strikingActions() !== undefined && this.autosync()) this.#layout.strikingActions.set(this._strikingActions());
        });
        effect(() => {
            if (this._externalLinkGroups() !== undefined && this.autosync()) this.#layout.externalLinkGroups.set(this._externalLinkGroups());
        });
        effect(() => {
            if (this._appMenuItems() !== undefined && this.autosync()) this.#layout.appMenuItems.set(this._appMenuItems());
        });
        effect(() => {
            if (this._menuItemsAutoCollapse() !== undefined && this.autosync()) this.#layout.menuItemsAutoCollapse.set(this._menuItemsAutoCollapse());
        });
    }
}
